import {Controller} from "@hotwired/stimulus"

const icon_map = {
  success: {class: "success", icon: "fa-check"},
  error: {class: "error", icon: "fa-times"},
  alert: {class: "error", icon: "fa-times"},
  warning: {class: "warning", icon: "fa-exclamation-triangle"},
  notice: {class: "info",  icon: "fa-info"},
  undo: {class: "info",  icon: "fa-info"}
}

class ToastController extends Controller {
  static targets = ["alert"]
  static values = {
    duration: {type: Number, default: 5000}
  }
  // Initialize timouts even if _activate_toast runs before connect()
  // This can happen if a toast is triggered early or controller connects late
  timeouts = new Set()

  connect() {
    // Listen for frontend-triggered toasts
    window.addEventListener("toast:add", this.add_toast.bind(this))
  }

  disconnect() {
    window.removeEventListener("toast:add", this.add_toast)
    this.timeouts.forEach((timeout) => clearTimeout(timeout))
    this.timeouts.clear()
  }

  alertTargetConnected(element) {
    const duration = this._get_toast_duration(element)
    this._handle_undo_stacking(element)
    this._activate_toast(element, duration)
  }

  add_toast(event) {
    const {
      title,
      type = "notice",
      subtitle = "",
      duration
    } = event.detail
    const toast = this._create_toast_element(
      title,
      type,
      subtitle,
      duration
    )
    this.element.prepend(toast)
  }

  dismiss(event) {
    const toast = event.target.closest('[data-toast-target="alert"]')
    this._clear_timeout(toast)
    this._remove_toast(toast)
  }

  _activate_toast(toast, duration) {
    toast.classList.add("active")
    if (!duration) return

    this._init_timer(toast, duration)
  }

  _init_timer(toast, duration) {
    toast.addEventListener("mouseenter", () => {
      this._pause_timer(toast)
    })
    toast.addEventListener("mouseleave", () => {
      this._start_timer(toast)
    })

    toast._is_paused = true
    toast._duration = duration
    toast._remaining_duration = duration
    this._start_timer(toast)
  }

  _start_timer(toast) {
    if (!toast._is_paused || toast._remaining_duration <= 0) return

    toast._is_paused = false
    toast._start_time = performance.now()
    const timeout = setTimeout(() => {
      this._remove_toast(toast, timeout)
    }, toast._remaining_duration)
    toast._timeout = timeout
    this.timeouts.add(timeout)
    this._update_timer_animation(toast)
  }

  _update_timer_animation(toast) {
    const bar = toast.querySelector("[data-toast-target='bar']")
    bar.style.transition = `transform ${toast._remaining_duration}ms linear`
    requestAnimationFrame(() => {
      bar.style.transform = "scaleX(0)"
    })
  }

  _pause_timer(toast) {
    if (toast._is_paused) return

    toast._is_paused = true
    this._clear_timeout(toast)
    const now = performance.now()
    const elapsed = now - toast._start_time
    toast._remaining_duration = Math.max(0, toast._remaining_duration - elapsed)
    const bar = toast.querySelector("[data-toast-target='bar']")
    bar.style.transition = "none"
    // Calculate how much of the original duration is left
    const current_scale = Math.max(
      0, toast._remaining_duration / toast._duration
    )
    bar.style.transform = `scaleX(${current_scale})`
  }

  _icon_data(type) {
    return icon_map[type] || icon_map.notice
  }

  _remove_toast(toast, timeout = null) {
    toast.classList.remove("active")
    setTimeout(() => {
      toast.remove()
      if (timeout) this.timeouts.delete(timeout)
    }, 300)
  }

  _clear_timeout(toast) {
    if (toast && toast._timeout) {
      clearTimeout(toast._timeout)
      this.timeouts.delete(toast._timeout)
      toast._timeout = null
    }
  }

  _handle_undo_stacking(node) {
    // Only show one undo toast at a time
    if (node.classList.contains("undo")) {
      this.alertTargets.forEach((toast) => {
        if (toast !== node && toast.classList.contains("undo")) {
          this._remove_toast(toast)
        }
      })
    }
  }

  _get_toast_duration(node) {
    const duration = node.dataset.toastDurationValue
    if (duration === "false") return false

    return duration ? parseInt(duration, 10) : this.durationValue
  }

  _create_toast_element(title, type, subtitle, duration) {
    const {class: icon_class, icon} = this._icon_data(type)
    const toast = document.createElement("div")
    toast.className = `toast ${type}`
    toast.setAttribute("data-toast-target", "alert")
    if (duration !== undefined) {
      toast.setAttribute("data-toast-duration-value", duration)
    }
    toast.innerHTML = `
      <div class="content">
        <div class="icon icon--${icon_class}">
          <svg class="fa ${icon}">
            <use href="${window.gon.icons_path}#${icon}"></use>
          </svg>
        </div>
        <div class="text">
          <div class="title">${title}</div>
          ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ""}
        </div>
      </div>
      ${duration !== false ?
        `<div class="timer-bar ${type}" data-toast-target="bar"></div>` :
        ""}
      <button class="close" data-action="click->toast#dismiss">
        <svg class="fa fa-close">
          <use href="${window.gon.icons_path}#fa-close"></use>
        </svg>
      </button>
    `
    return toast
  }
}

export default ToastController
